import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, Quote, Slate } from "../components/Bits";
import { WelcomeReel } from "../components/WelcomeReel";
import { DeckButton } from "../components/DeckButton";
import { TRAILER_URL, SHOW, IMG, QUOTES, ARC, BEATS, EPISODES, PREVIEW_BEATS, PREVIEW_EPISODES } from "../data/show";

const Hero = () => (
  <section className="hero" data-testid="home-hero">
    <div className="hero-bg" style={{ backgroundImage: `url(${IMG.hero})` }} />
    <div className="hero-shade" />
    <div className="wrap w-full relative z-10 pt-32 pb-16 md:pt-36 md:pb-20">
      <Reveal>
        <p className="slate text-white/80">{SHOW.format} · Created for Samantha</p>
        <h1 className="display hero-title mt-5" data-testid="home-title">Then Came<br /><em>Sam</em></h1>
        <p className="mt-8 max-w-xl text-base md:text-lg text-white/85 leading-relaxed" data-testid="home-logline">{SHOW.logline}</p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/episodes#pilot" className="btn btn-sun" data-testid="hero-pilot-beats-btn">Watch the Pilot Beats <ArrowRight size={16} /></Link>
          <Link to="/characters" className="btn btn-ghost" data-testid="hero-meet-cast-btn">Meet the Cast</Link>
          <DeckButton testid="hero-download-deck-btn" className="btn btn-ghost" />
        </div>
      </Reveal>
      <p className="slate text-white/60 mt-14 max-w-md" data-testid="hero-opening-visual">Opening visual — {SHOW.openingVisual}</p>
    </div>
  </section>
);

const Trailer = () => {
  const [clicked, setClicked] = useState(false);
  return (
    <section className="section bg-ink text-white" data-testid="trailer-section">
      <div className="wrap">
        <Reveal><Eyebrow light>Official Trailer</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">Watch the Trailer</h2></Reveal>
        <Reveal delay={0.1}>
          {clicked && TRAILER_URL ? (
            <video className="trailer-video" src={TRAILER_URL} controls autoPlay playsInline data-testid="trailer-video" />
          ) : (
          <button className="trailer" onClick={() => setClicked(true)} data-testid="trailer-play-btn" style={{ backgroundImage: `url(${IMG.pr})` }}>
            <span className="trailer-shade" />
            <span className="play-disc"><Play size={30} fill="currentColor" /></span>
            <span className="slate absolute right-6 top-6 text-white/85" data-testid="trailer-status">
              {clicked ? "Trailer coming soon — video link to be added" : "Click to watch trailer"}
            </span>
          </button>
          )}
        </Reveal>
      </div>
    </section>
  );
};

const SeriesArc = () => (
  <section className="section" data-testid="series-arc-section">
    <div className="wrap">
      <Reveal><Eyebrow>The Journey</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">The Series Arc</h2>
        <p className="mt-4 max-w-xl text-[var(--muted)]">Three seasons: from a man who thinks he owns a house to the cat who owns the coast.</p></Reveal>
      <div className="arc-track mt-14">
        {ARC.map((a, i) => (
          <Reveal key={a.season} delay={i * 0.12} className="arc-node" data-testid={`arc-season-${i + 1}`}>
            <span className="arc-num">0{i + 1}</span>
            <p className="slate mt-6">{a.season} · {a.tag}</p>
            <h3 className="display text-3xl mt-2">{a.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{a.text}</p>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10"><Link to="/about#arc" className="text-link" data-testid="arc-read-more-link">Read the full series arc <ArrowUpRight size={16} /></Link></Reveal>
    </div>
  </section>
);

const Card = ({ kicker, title, text, to, testid, cta }) => (
  <Reveal className="preview-card" data-testid={testid}>
    <p className="slate">{kicker}</p>
    <h3 className="display text-2xl mt-3">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-[var(--muted)] line-clamp-4">{text}</p>
    <Link to={to} className="text-link mt-6" data-testid={`${testid}-link`}>{cta} <ArrowRight size={14} /></Link>
  </Reveal>
);

const Previews = () => {
  const beats = BEATS.filter((b) => PREVIEW_BEATS.includes(b.id));
  const eps = EPISODES.filter((e) => PREVIEW_EPISODES.includes(e.n));
  return (
    <section className="section bg-sand" data-testid="previews-section">
      <div className="wrap">
        <Reveal><Eyebrow>15 Beats · 10 Episodes</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">Inside the Show</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {beats.map((b) => <Card key={b.id} kicker={`Pilot · Beat ${b.n} · ${b.act}`} title={b.title} text={b.text} to={`/episodes#${b.id}`} cta="Read this beat" testid={`preview-${b.id}`} />)}
          {eps.map((e) => <Card key={e.n} kicker={`Episode ${e.n} · Escalation ${e.level}/10`} title={e.title} text={e.crisis} to={`/episodes/${e.n}`} cta="Read this episode" testid={`preview-ep-${e.n}`} />)}
        </div>
        <div className="mt-12"><Link to="/episodes" className="btn btn-ink" data-testid="read-all-episodes-btn">Read the Full Beat Sheet <ArrowRight size={16} /></Link></div>
      </div>
    </section>
  );
};

const Beyond = () => (
  <section className="section" data-testid="beyond-section">
    <div className="wrap grid md:grid-cols-2 gap-14 items-center">
      <Reveal>
        <Eyebrow>Beyond the Show</Eyebrow>
        <h2 className="display text-3xl md:text-5xl mt-3">The Welcome Committee</h2>
        <p className="mt-6 text-[var(--muted)] leading-relaxed">Every visitor to the hilltop is screened by the house's chief of security. Clearance costs one kiss, with no exceptions and no appeals. The Welcome Committee is the show's signature: a door-side ritual that grows each week, from a single delivery driver to a line of studio executives down the drive.</p>
        <Link to="/characters#samantha" className="text-link mt-8" data-testid="beyond-meet-samantha-link">Meet Samantha <ArrowUpRight size={16} /></Link>
      </Reveal>
      <Reveal delay={0.15}><Slate src={IMG.welcome} label="Samantha / Welcome Committee" testid="beyond-welcome-image" className="aspect-[459/428]" /></Reveal>
    </div>
  </section>
);

const Closing = () => (
  <section className="closing" data-testid="closing-section">
    <div className="closing-bg" style={{ backgroundImage: `url(${IMG.hilltop})` }} />
    <div className="closing-shade" />
    <div className="wrap w-full relative z-10 py-32 md:py-44">
      <Reveal className="max-w-3xl"><Quote q={QUOTES.closing} testid="closing-quote" dark /></Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Trailer />
      <section className="section-tight"><div className="wrap"><Reveal className="max-w-4xl"><Quote q={QUOTES.featured} testid="featured-quote" /></Reveal></div></section>
      <SeriesArc />
      <Previews />
      <section className="section-tight bg-ink text-white"><div className="wrap"><Reveal className="max-w-4xl"><Quote q={QUOTES.mid} testid="mid-quote" dark /></Reveal></div></section>
      <Beyond />
      <WelcomeReel />
      <Closing />
    </div>
  );
}
